'use client';

import { useTransition } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ps/ui/atoms/alert-dialog';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /**
   * Runs the soft-delete. Resolves once the API call succeeds — the
   * bubble catches up to the "deleted" placeholder via its Firestore
   * message subscription.
   */
  onConfirm: () => Promise<void>;
}

/**
 * Confirmation dialog for deleting a channel message — mirrors
 * PopshopRN's "Delete Message" alert (the kebab's Delete never deletes
 * without confirmation). Styled with explicit dark colors like the
 * sibling {@link DeleteChannelDialog} because the dashboard shell isn't
 * `.dark`-scoped and `AlertDialog` portals to `body`.
 */
export function DeleteChannelMessageDialog({
  open,
  onOpenChange,
  onConfirm,
}: Props) {
  const [pending, startTransition] = useTransition();

  const handleConfirm = () => {
    if (pending) return;
    startTransition(async () => {
      try {
        await onConfirm();
        onOpenChange(false);
      } catch (error) {
        console.error('delete channel message failed', error);
      }
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="text-content-1 border-line bg-surface-1">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-content-1">
            Delete message?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-content-2">
            This message will be removed for everyone. This can&rsquo;t be
            undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={pending}
            className="text-content-1 border-line-strong bg-surface-1 hover:bg-brand-hover-soft"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={event => {
              // Keep the dialog mounted until the async delete settles;
              // Radix would otherwise close it on click before onConfirm runs.
              event.preventDefault();
              handleConfirm();
            }}
            className="bg-danger text-content-on-danger hover:bg-red-600"
          >
            {pending ? 'Deleting…' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
